import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QButton, QModalHeader, QStack, QText, useCurrentUser } from '@qualio/ui-components';
import { QLookUpUserWrapper } from '../../../../QLookUpUserWrapper/QLookUpUserWrapper';
import { useEligibleCollaborators } from '../../../../../hooks/useEligibleCollaborators';
import { useGroups } from '../../../../../hooks/useGroups';
import { useTags } from '../../../../../hooks/useTags';
import { useUpdateCollaborators } from '../../../../../hooks/useUpdateCollaborators';
import { useUsers } from '../../../../../hooks/useUsers';
import { Collaborator, QualityEvent, User } from '../../../../../types';
import { ManageCollaboratorsConfirmationModal } from './ManageCollaboratorsConfirmationModal';

const isNeverDisabled = () => false;

type ManageCollaboratorsButtonProps = {
  event: QualityEvent;
};

export const ManageCollaboratorsButton = ({ event }: ManageCollaboratorsButtonProps) => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const { data: users, isLoading: isLoadingUsers } = useUsers();
  const { data: groups, isLoading: isLoadingGroups } = useGroups();
  const { data: tags, isLoading: isLoadingTags } = useTags();
  const eligibleUsers = useEligibleCollaborators(users ?? [], tags, event.tags_ids, event.owner_id);
  const { updateCollaborators } = useUpdateCollaborators(companyId, event.id);
  const [isLookupOpen, setIsLookupOpen] = useState(false);
  const [selection, setSelection] = useState<readonly User[]>();

  const currentIds = useMemo(() => new Set(event.collaborators.map(({ id }) => id)), [event.collaborators]);
  const selectedIds = useMemo(() => selection && new Set(selection.map(({ id }) => id)), [selection]);

  const isCurrentCollaborator = useCallback((user: User) => currentIds.has(user.id), [currentIds]);
  const isSelectedOnOpen = useCallback(
    (user: User) => (selectedIds ?? currentIds).has(user.id),
    [selectedIds, currentIds],
  );

  const added: Collaborator[] = useMemo(
    () =>
      (selection ?? [])
        .filter(({ id }) => !currentIds.has(id))
        .map(({ id, full_name }) => ({ id, full_name, is_active: true })),
    [selection, currentIds],
  );
  const removed = useMemo(
    () => event.collaborators.filter(({ id }) => !selectedIds?.has(id)),
    [event.collaborators, selectedIds],
  );

  const close = useCallback(() => {
    setIsLookupOpen(false);
    setSelection(undefined);
  }, []);

  const continueToConfirmation = useCallback(async (selected: readonly User[]) => {
    setSelection(selected);
    setIsLookupOpen(false);
  }, []);

  const applyChanges = useCallback(
    async (comment: string) => {
      await updateCollaborators({ collaborators: (selection ?? []).map(({ id }) => id), comment });
      close();
    },
    [updateCollaborators, selection, close],
  );

  return (
    <>
      <QButton
        data-cy="manage-collaborators-button"
        variant="link"
        size="xs"
        isDisabled={isLoadingUsers || isLoadingGroups || isLoadingTags}
        onClick={() => setIsLookupOpen(true)}
      >
        {t('common.labels.manage')}
      </QButton>
      {isLookupOpen && (
        <QLookUpUserWrapper
          groups={groups ?? []}
          data={eligibleUsers}
          isItemDisabled={isNeverDisabled}
          isItemPreSelected={isCurrentCollaborator}
          isItemSelectedOnOpen={isSelectedOnOpen}
          handleOnSelect={continueToConfirmation}
          onClose={close}
          isOpen
        >
          <QModalHeader>
            <QStack spacing="2">
              <QText>{t('events.messages.manageCollaborators')}</QText>
              <QText fontSize="md" fontWeight="normal">
                {t('events.messages.selectUsersForCollaboration')}
              </QText>
            </QStack>
          </QModalHeader>
        </QLookUpUserWrapper>
      )}
      {selection && !isLookupOpen && (
        <ManageCollaboratorsConfirmationModal
          added={added}
          removed={removed}
          onClose={close}
          onBack={() => setIsLookupOpen(true)}
          onSubmit={applyChanges}
        />
      )}
    </>
  );
};
